import { Component, inject, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TableFilterChangeEvent,
  TablePagination,
  TableSearchEvent,
  TableSort,
} from "../../../../Common/Components/Table/Type/TableType";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import {
  TestimonyFollowupItem,
  TestimonyFollowupStatus,
  TestimonyHomeView,
  TestimonyReportEntity,
  TestimonyReportStatus,
} from "../../Dto/TestimonyModel";
import { TestimonyEffects } from "../../State/TestimonyEffects";
import { TestimonyState } from "../../State/TestimonyState";

@Component({
  selector: "app-testimony-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./TestimonyHomePage.html",
  styleUrl: "./TestimonyHomePage.scss",
  encapsulation: ViewEncapsulation.None,
})
export class TestimonyHomePage implements OnInit {
  private readonly testimonyEffects = inject(TestimonyEffects);
  readonly testimonyState = inject(TestimonyState);
  private readonly toastEffects = inject(ToastEffects);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  activeView: TestimonyHomeView = "testimonies";
  selectedReportStatusFilter = "";
  readonly statusOptions: { value: TestimonyReportStatus; label: string }[] = [
    { value: "Draft", label: "Draft" },
    { value: "Contacted", label: "Contacted" },
    { value: "PartialReport", label: "Partial report" },
    { value: "Unresponsive", label: "Unresponsive" },
    { value: "Submitted", label: "Submitted" },
    { value: "SyncFailed", label: "Sync failed" },
  ];

  readonly followupColumns: TableColumn<TestimonyFollowupItem>[] = [
    {
      key: "priorityScore",
      header: "Priority",
      sortable: true,
      width: "12%",
      cellTemplate: (item) => `${item.priorityScore} (${item.daysSinceDelivered}d x ${item.totalDeliveredQty})`,
    },
    { key: "requestNo", header: "Request", sortable: true, width: "15%" },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "14%",
      cellTemplate: (item) => this.getFollowupStatusLabel(item.status),
      cellClass: (item) => this.getFollowupStatusChipClass(item.status),
      cellIcon: (item) => this.getFollowupStatusChipIcon(item.status),
    },
    {
      key: "teamLeader.name",
      header: "Team Leader",
      sortable: false,
      width: "18%",
      cellTemplate: (item) => item.teamLeader?.name || "-",
    },
    {
      key: "partner.name",
      header: "Institution",
      sortable: false,
      width: "22%",
      cellTemplate: (item) => item.partner?.name || "-",
    },
    {
      key: "deliveredAt",
      header: "Delivered",
      sortable: true,
      width: "18%",
      cellTemplate: (item) => this.formatDate(item.deliveredAt),
    },
    {
      key: "totalDeliveredQty",
      header: "Qty",
      sortable: true,
      width: "10%",
      align: "right",
    },
  ];

  readonly reportColumns: TableColumn<TestimonyReportEntity>[] = [
    {
      key: "requestNo",
      header: "Request",
      sortable: true,
      width: "15%",
      cellTemplate: (item) => item.requestNo || item.requestId,
    },
    {
      key: "teamLeader.name",
      header: "Team Leader",
      sortable: false,
      width: "17%",
      cellTemplate: (item) => item.teamLeader?.name || "-",
    },
    {
      key: "partner.name",
      header: "Institution",
      sortable: false,
      width: "20%",
      cellTemplate: (item) => item.partner?.name || "-",
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "13%",
      cellTemplate: (item) => this.getStatusLabel(item.status),
      cellClass: (item) => this.getStatusChipClass(item.status),
      cellIcon: (item) => this.getStatusChipIcon(item.status),
    },
    {
      key: "reportDate",
      header: "Report Date",
      sortable: true,
      width: "15%",
      cellTemplate: (item) => this.formatDate(item.reportDate),
    },
    {
      key: "usedQty",
      header: "Used",
      sortable: false,
      width: "8%",
      align: "right",
      cellTemplate: (item) => this.getTotalUsed(item),
    },
    {
      key: "leftQty",
      header: "Left",
      sortable: false,
      width: "8%",
      align: "right",
      cellTemplate: (item) => this.getTotalLeft(item),
    },
    {
      key: "syncError",
      header: "Sync Error",
      sortable: false,
      width: "20%",
      visibleByDefault: false,
      cellTemplate: (item) => item.dcmSync?.error || "-",
      cellClass: (item) =>
        (() => {
          if (item.dcmSync?.error) {
            return "testimony-home__sync-error";
          }
          return "";
        })(),
    },
  ];

  get followupConfig(): TableConfig<TestimonyFollowupItem> {
    return {
      loading: this.testimonyState.loading(),
      emptyMessage: this.testimonyState.error() || "No testimonies waiting for report.",
      showActions: true,
      actionsWidth: "80px",
      showSearch: true,
      searchPlaceholder: "Search testimonies...",
      actionButtons: [
        {
          icon: "add_notes",
          label: "Open/Create Report",
          handler: (item) => {
            void this.createReport(item);
          },
        },
      ],
    };
  }

  get reportConfig(): TableConfig<TestimonyReportEntity> {
    return {
      loading: this.testimonyState.loading(),
      emptyMessage: this.testimonyState.error() || "No testimony reports found.",
      showActions: true,
      actionsWidth: "80px",
      showSearch: true,
      searchPlaceholder: "Search reports...",
      filters: [
        {
          key: "status",
          label: "Status",
          placeholder: "All status",
          options: this.statusOptions,
          defaultValue: this.selectedReportStatusFilter,
        },
      ],
      actionButtons: [
        {
          icon: "open_in_new",
          label: "Open",
          handler: (item) => this.editReport(item),
        },
      ],
    };
  }

  get followupPagination(): TablePagination {
    return this.testimonyState.followupPagination();
  }

  get reportPagination(): TablePagination {
    return this.testimonyState.reportPagination();
  }

  ngOnInit(): void {
    this.selectedReportStatusFilter = this.testimonyState.reportSearchParams().status ?? "";
    this.route.queryParamMap.subscribe((params) => {
      this.activeView = this.parseView(params.get("view"));
    });
    void this.testimonyEffects.searchFollowups(this.testimonyState.followupSearchParams());
    void this.testimonyEffects.searchReports(this.testimonyState.reportSearchParams());
  }

  onFollowupPageChange(event: { page: number; search?: string }): void {
    const current = this.testimonyState.followupSearchParams();
    void this.testimonyEffects.searchFollowups({ ...current, page: event.page, search: event.search });
  }

  onFollowupPageSizeChange(event: { limit: number; search?: string }): void {
    const current = this.testimonyState.followupSearchParams();
    void this.testimonyEffects.searchFollowups({ ...current, page: 1, limit: event.limit, search: event.search });
  }

  onFollowupSearchChange(event: TableSearchEvent): void {
    const current = this.testimonyState.followupSearchParams();
    void this.testimonyEffects.searchFollowups({ ...current, search: event.term, page: event.page });
  }

  onReportPageChange(event: { page: number; search?: string; filters?: Record<string, string> }): void {
    const current = this.testimonyState.reportSearchParams();
    const status = (event.filters?.["status"] ?? this.selectedReportStatusFilter) as TestimonyReportStatus | "";
    void this.testimonyEffects.searchReports({
      ...current,
      page: event.page,
      search: event.search,
      status: status || undefined,
    });
  }

  onReportPageSizeChange(event: { limit: number; search?: string; filters?: Record<string, string> }): void {
    const current = this.testimonyState.reportSearchParams();
    const status = (event.filters?.["status"] ?? this.selectedReportStatusFilter) as TestimonyReportStatus | "";
    void this.testimonyEffects.searchReports({
      ...current,
      page: 1,
      limit: event.limit,
      search: event.search,
      status: status || undefined,
    });
  }

  onReportSearchChange(event: TableSearchEvent): void {
    const current = this.testimonyState.reportSearchParams();
    const status = (event.filters?.["status"] ?? this.selectedReportStatusFilter) as TestimonyReportStatus | "";
    void this.testimonyEffects.searchReports({
      ...current,
      search: event.term,
      status: status || undefined,
      page: event.page,
    });
  }

  onReportFilterChange(event: TableFilterChangeEvent): void {
    this.selectedReportStatusFilter = event.filters["status"] ?? "";
    const current = this.testimonyState.reportSearchParams();
    const status = this.selectedReportStatusFilter.trim() as TestimonyReportStatus | "";
    void this.testimonyEffects.searchReports({
      ...current,
      status: status || undefined,
      search: event.term,
      page: event.page,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    return;
  }

  async createReport(item: TestimonyFollowupItem): Promise<void> {
    try {
      const report = await this.testimonyEffects.createFromRequest(item.requestId);
      await this.router.navigate(["/testimonies/edit", report.id]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create testimony report."), "error");
    }
  }

  editReport(item: TestimonyReportEntity): void {
    this.router.navigate(["/testimonies/edit", item.id]).catch(() => undefined);
  }

  setView(view: TestimonyHomeView): void {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { view },
        queryParamsHandling: "merge",
      })
      .catch(() => undefined);
  }

  formatDate(value?: string): string {
    return (() => {
      if (value) {
        return formatDateNumeric(value);
      }
      return "-";
    })();
  }

  private getStatusLabel(status: TestimonyReportStatus): string {
    if (status === "PartialReport") return "Partial report";
    if (status === "SyncFailed") return "Sync failed";
    return status;
  }

  private getStatusChipClass(status: TestimonyReportStatus): string {
    if (status === "Draft") return "app-table__chip app-table__chip--neutral";
    if (status === "Contacted") return "app-table__chip app-table__chip--info";
    if (status === "PartialReport") return "app-table__chip app-table__chip--warning";
    if (status === "Unresponsive") return "app-table__chip app-table__chip--danger";
    if (status === "Submitted") return "app-table__chip app-table__chip--success";
    return "app-table__chip app-table__chip--danger";
  }

  private getStatusChipIcon(status: TestimonyReportStatus): string {
    if (status === "Draft") return "schedule";
    if (status === "Contacted") return "chat";
    if (status === "PartialReport") return "pending_actions";
    if (status === "Unresponsive") return "phone_disabled";
    if (status === "Submitted") return "check_circle";
    return "cancel";
  }

  private getFollowupStatusLabel(status: TestimonyFollowupStatus): string {
    return (() => {
      if (status === "NotStarted") {
        return "Not started";
      }
      return this.getStatusLabel(status);
    })();
  }

  private getFollowupStatusChipClass(status: TestimonyFollowupStatus): string {
    return (() => {
      if (status === "NotStarted") {
        return "app-table__chip app-table__chip--neutral";
      }
      return this.getStatusChipClass(status);
    })();
  }

  private getFollowupStatusChipIcon(status: TestimonyFollowupStatus): string {
    return (() => {
      if (status === "NotStarted") {
        return "schedule";
      }
      return this.getStatusChipIcon(status);
    })();
  }

  getTotalUsed(report: TestimonyReportEntity): number {
    return report.materials.reduce((total, item) => total + Number(item.usedQty ?? 0), 0);
  }

  getTotalLeft(report: TestimonyReportEntity): number {
    return report.materials.reduce((total, item) => total + Number(item.leftQty ?? 0), 0);
  }

  private parseView(value: string | null): TestimonyHomeView {
    return (() => {
      if (value === "reports") {
        return "reports";
      }
      return "testimonies";
    })();
  }
}
